import React, { useContext, useState } from 'react';
import { DeleteCategoryModalView, DeleteCategoryModalViewProps } from './DeleteCategoryModal.view';
import { ApolloError, gql, useApolloClient } from '@apollo/client';
import {
  Category,
  CategoryArchiveFilter,
  DeleteCategoryMutationFn,
  PlanUsageType,
  UpdateCategoryInput,
  UpdateCategoryMutationFn,
  UsageFieldsFragmentDoc
} from '../../../../models/graphql';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { showToast } from '../../../../helpers/showToast';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import {
  PostingPlanFiltersEnum,
  PostLibraryFiltersEnum,
  PostScheduleFiltersEnum,
  removeRefFilterValueFromFiltersLocalStorage
} from '../../../../helpers/filterStorageHelpers';
import { AppContext } from 'providers/AppProvider';

export type DeleteCategoryModalControllerProps = Omit<DeleteCategoryModalViewProps, 'submitted' | 'onSubmit'> & {
  categoryId: string;
  deleteCategory: DeleteCategoryMutationFn;
  updateCategory: UpdateCategoryMutationFn;
};

export const DeleteCategoryModalController = (props: DeleteCategoryModalControllerProps): JSX.Element => {
  const { cache } = useApolloClient();
  const { setPollUsage } = useContext(AppContext);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const { selectedWorkspace } = useSelectedOrganization();
  const workspaceId = selectedWorkspace?.id ?? '';

  const onSubmit = () => {
    setSubmitted(true);

    if (props.isArchived) {
      props
        .deleteCategory({
          variables: { categoryId: props.categoryId },
          update: cache => {
            cache.evict({ id: cache.identify({ id: props.categoryId, __typename: 'Category' }) });
            cache.gc();

            props.onClose();
          }
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          handleResponseError(errorForRemapping, 'Error in deleting the category');
        });
    } else {
      const input: UpdateCategoryInput = {
        archived: true
      };

      props
        .updateCategory({
          variables: { categoryId: props.categoryId, input: input },
          update: () => {
            removeRefFilterValueFromFiltersLocalStorage(
              [
                { group: 'PostLibraryFilters', key: PostLibraryFiltersEnum.postScheduleCategoryFilter },
                { group: 'PostScheduleFilters', key: PostScheduleFiltersEnum.postScheduleCategoryFilter },
                { group: 'PostingPlanFilters', key: PostingPlanFiltersEnum.categoryFilter }
              ],
              props.categoryId,
              workspaceId
            );
            props.onClose();
          }
        })
        .then(() => {
          setPollUsage({ amountPoll: 2, interval: 3000, shouldPoll: true }); // to update the post usage
          // Filter this category from the active cache.
          cache.modify({
            id: cache.identify({ id: workspaceId, __typename: 'Workspace' }),
            fields: {
              [`categories:{"statusFilter":"${CategoryArchiveFilter.ACTIVE}"}`](existing, { readField }) {
                return existing.filter((category: Category) => readField('id', category) !== props.categoryId);
              }
            }
          });
          // Add this category reference to the archived cache.
          cache.modify({
            id: cache.identify({ id: workspaceId, __typename: 'Workspace' }),
            fields: {
              [`categories:{"statusFilter":"${CategoryArchiveFilter.ARCHIVED}"}`](existing) {
                // This generates a reference we can use.
                const newRef = cache.writeFragment({
                  data: { id: props.categoryId, __typename: 'Category' },
                  fragment: gql`
                    fragment Category on Category {
                      id
                    }
                  `
                });
                return [...existing, newRef];
              }
            }
          });
          const oldUsage: PlanUsageType | null = cache.readFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}'
          });
          const newUsage = { ...oldUsage, activeCategories: (oldUsage?.activeCategories ?? 0) - 1 };
          cache.writeFragment({
            fragment: UsageFieldsFragmentDoc,
            fragmentName: 'UsageFields',
            id: 'PlanUsageType:{}',
            data: newUsage
          });
        })
        .catch(e => {
          const errorForRemapping = e as ApolloError;
          handleResponseError(errorForRemapping, 'Error in archiving the category');
        });
    }
  };

  const handleResponseError = (e: ApolloError, title: string) => {
    const mappedError = remapApolloErrors(e);
    const errorMessage = getApolloErrorMessage(e);

    if (errorMessage === 'NOT_FOUND') {
      cache.evict({ id: cache.identify({ id: props.categoryId, __typename: 'Category' }) });
      cache.gc();

      props.onClose();
      return;
    }

    showToast({
      summary: title,
      detail: mappedError,
      severity: 'error'
    });

    setSubmitted(false);
  };

  const viewProps: DeleteCategoryModalViewProps = { ...props, onSubmit, submitted };

  return <DeleteCategoryModalView {...viewProps} />;
};
